import api from './api';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.designinfra.co.in/api';

// The API base minus the trailing `/api` — images are served from
// {host}/storage/{path}, not from under the API prefix.
const STORAGE_BASE = `${API_URL.replace(/\/api\/?$/, '')}/storage`;

/**
 * Expand a stored image path (`team-members/a1b2c3.jpg`) into a URL.
 * Returns null when the member has no photo, so callers can branch on it.
 */
export const teamMemberImageUrl = (image) =>
  image ? `${STORAGE_BASE}/${image}` : null;

/**
 * Normalize a raw API member to the shape used by the UI.
 * API fields: id, name, designation, image, duration, experience, is_active
 */
const normalizeMember = (raw) => ({
  id: raw.id,
  name: raw.name ?? '',
  designation: raw.designation ?? '',
  image: raw.image ?? null,
  // Nullable on the API; '' keeps the inputs controlled.
  duration: raw.duration ?? '',
  experience: raw.experience ?? '',
  isActive: raw.is_active !== undefined ? Boolean(Number(raw.is_active)) : Boolean(raw.isActive),
  createdAt: raw.created_at ?? null,
});

/**
 * Build a multipart/form-data payload from UI values.
 * Only appends `image` when a real File is provided, so saving without
 * touching the photo leaves the existing one in place.
 */
const buildFormData = ({ name, designation, image, duration, experience, isActive }) => {
  const fd = new FormData();
  fd.append('name', name ?? '');
  fd.append('designation', designation ?? '');
  fd.append('duration', duration ?? '');
  fd.append('experience', experience ?? '');
  fd.append('is_active', isActive ? '1' : '0');
  if (image instanceof File) {
    fd.append('image', image);
  }
  return fd;
};

export const teamMemberService = {
  // No `search` param — the backend implements none for this module,
  // so TeamMembersPage filters the list locally.
  getAll: async (params = {}) => {
    const response = await api.get('/team-members', { params });
    const raw = response.data;
    const list = Array.isArray(raw) ? raw : (raw.data ?? []);
    const members = list.map(normalizeMember);
    return { data: { members, total: members.length } };
  },

  getById: async (id) => {
    const response = await api.get(`/team-members/${id}`);
    return { data: normalizeMember(response.data?.data ?? response.data) };
  },

  create: async (data) => {
    const response = await api.post('/team-members', buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: normalizeMember(response.data?.data ?? response.data) };
  },

  // POST, not PUT — PHP does not parse multipart on PUT, so the file would be lost.
  update: async (id, data) => {
    const response = await api.post(`/team-members/update/${id}`, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: normalizeMember(response.data?.data ?? response.data) };
  },

  delete: async (id) => api.delete(`/team-members/${id}`),

  toggleStatus: async (id) => api.patch(`/team-members/toggle/${id}`),
};

export default teamMemberService;
